// HTML ⇄ Tiptap JSON, for surfaces whose storage is already an HTML string.
//
// The editor's own contract is `JSONContent` in and `JSONContent` out, which
// is right for anything that will ever grow versions or block-level storage.
// But a surface that already persists HTML — the sticky note keeps one HTML
// string per note in localStorage — should not be forced into a storage
// migration just to stop using `document.execCommand`. So the conversion
// happens here, once, against the SAME extension list the editor will mount,
// rather than in each consumer against a guessed one.
//
// Only the lean tiers are supported on purpose: `full` includes node views
// (images, files, embeds, tables, field blocks) whose HTML round-trip is not
// lossless, and nothing in the app stores a full document as HTML.

import { generateJSON, generateHTML } from '@tiptap/html';
import type { JSONContent } from '@tiptap/core';
import { buildLeanExtensions, type EditorTier } from './editor-tiers';

const EMPTY_DOC: JSONContent = { type: 'doc', content: [{ type: 'paragraph' }] };

/**
 * Old sticky-note checklists, rewritten so Tiptap can see them.
 *
 * The pre-Tiptap toolbar built a checklist by inserting raw markup:
 * `<div class="flex…"><input type="checkbox"><span>text</span></div>`. That is
 * not in any schema, so a straight parse would drop the `<input>` silently and
 * the note would come back as plain lines — the text survives, the ticks do
 * not. This maps each one onto a real task item before parsing, so notes
 * written under the old editor keep their checkboxes.
 *
 * Deliberately a string rewrite and not a DOM walk: it runs once per note on
 * mount, and the shape it matches is the one shape that toolbar ever emitted.
 */
export function normalizeLegacyStickyHtml(html: string): string {
  if (!html.includes('type="checkbox"')) return html;

  const item = /<div[^>]*>\s*<input[^>]*type="checkbox"([^>]*)>\s*<span[^>]*>([\s\S]*?)<\/span>\s*<\/div>/gi;
  const converted = html.replace(item, (_m, attrs: string, text: string) => {
    const checked = /\bchecked\b/i.test(attrs) ? 'true' : 'false';
    return `<li data-type="taskItem" data-checked="${checked}"><p>${text}</p></li>`;
  });

  if (converted === html) return html;

  // Wrap each run of adjacent converted items in one task list.
  return converted.replace(
    /(?:<li data-type="taskItem"[\s\S]*?<\/li>\s*)+/g,
    (run) => `<ul data-type="taskList">${run}</ul>`,
  );
}

/** Parse a stored HTML string into a document for the given tier. */
export function htmlToDoc(html: string, tier: Exclude<EditorTier, 'full'> = 'basic'): JSONContent {
  const trimmed = (html ?? '').trim();
  if (!trimmed) return EMPTY_DOC;
  return generateJSON(trimmed, buildLeanExtensions(tier)) as JSONContent;
}

/** Serialize a document back to the HTML string the surface persists. */
export function docToHtml(doc: JSONContent, tier: Exclude<EditorTier, 'full'> = 'basic'): string {
  return generateHTML(doc, buildLeanExtensions(tier));
}
